/**
 * Bouquet Finder — inline bouquet-picker quiz (MFS-5325).
 *
 * Steps are rendered by sections/bouquet-finder_quiz.liquid from section blocks;
 * this element only drives navigation and the result fetch.
 *
 * Results come from one Section Rendering API request per filter combination:
 *   GET /collections/<handle>?sections=collection__main&sort_by=...&[filters]
 * so the cards are the theme's own product cards, with no per-product fetches.
 *
 * Fallback order when fewer than `limit` cards come back (price is always kept):
 *   1. price + colour + style
 *   2. price + colour
 *   3. price
 */
class BouquetQuiz extends HTMLElement {
  static ADVANCE_DELAY = 280;
  // air between the sticky header and the top of the card when retaking
  static SCROLL_GAP = 16;

  connectedCallback() {
    this.collection = this.dataset.collection || 'all-flowers';
    this.limit = parseInt(this.dataset.limit, 10) || 8;
    this.sort = this.dataset.sort || 'best-selling';
    this.subscriptionLabel = this.dataset.subscriptionLabel || '';

    this.card = this.querySelector('[data-quiz-card]');
    this.steps = Array.from(this.querySelectorAll('[data-quiz-step]'));
    this.resultsEl = this.querySelector('[data-quiz-results]');
    this.gridEl = this.querySelector('[data-quiz-grid]');
    this.loadingEl = this.querySelector('[data-quiz-loading]');
    this.emptyTpl = this.querySelector('template[data-quiz-empty]');
    this.errorTpl = this.querySelector('template[data-quiz-error]');

    if (!this.card || this.steps.length === 0) return;

    this.answers = this._emptyAnswers();
    this.currentStep = 1;
    // one entry per collection URL; holds the in-flight promise so parallel
    // callers share a single request
    this._htmlCache = new Map();

    this.addEventListener('click', this._onClick);
    this.showStep(1);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _emptyAnswers() {
    return { none: [], priceMin: null, priceMax: null, color: null, style: null };
  }

  /* ── Navigation ─────────────────────────────────────────── */

  _onClick = (event) => {
    const option = event.target.closest('[data-quiz-option]');
    if (option && this.contains(option)) {
      this._answer(option);
      return;
    }

    if (event.target.closest('[data-quiz-skip]')) {
      this._clearAnswerFor(this.currentStep);
      this._advance();
      return;
    }

    if (event.target.closest('[data-quiz-back]')) {
      this._clearAnswerFor(this.currentStep);
      if (this.currentStep > 1) this.showStep(this.currentStep - 1);
      return;
    }

    if (event.target.closest('[data-quiz-retake]')) {
      // QA: retaking must not wipe the bouquets already on screen
      this.reset({ keepResults: true });
    }
  };

  _answer(button) {
    const stepEl = button.closest('[data-quiz-step]');
    const filter = stepEl ? stepEl.dataset.quizFilter : 'none';
    const value = button.dataset.value || button.textContent.trim();

    switch (filter) {
      case 'price': {
        const min = parseFloat(button.dataset.priceMin);
        const max = parseFloat(button.dataset.priceMax);
        this.answers.priceMin = isNaN(min) ? null : min;
        this.answers.priceMax = isNaN(max) ? null : max;
        break;
      }
      case 'color':
        this.answers.color = value;
        break;
      case 'style':
        this.answers.style = value;
        break;
      default:
        this.answers.none[this.currentStep] = value;
    }

    stepEl
      .querySelectorAll('[data-quiz-option]')
      .forEach((el) => el.classList.toggle('bf-quiz__option--selected', el === button));

    // starting a new run from step 1 clears the previous results
    if (this.currentStep === 1 && !this.resultsEl.hidden) {
      this.resultsEl.hidden = true;
      this.gridEl.innerHTML = '';
    }

    window.setTimeout(() => this._advance(), BouquetQuiz.ADVANCE_DELAY);
  }

  _clearAnswerFor(stepNumber) {
    const stepEl = this.steps[stepNumber - 1];
    if (!stepEl) return;

    switch (stepEl.dataset.quizFilter) {
      case 'price':
        this.answers.priceMin = null;
        this.answers.priceMax = null;
        break;
      case 'color':
        this.answers.color = null;
        break;
      case 'style':
        this.answers.style = null;
        break;
      default:
        delete this.answers.none[stepNumber];
    }

    stepEl
      .querySelectorAll('[data-quiz-option]')
      .forEach((el) => el.classList.remove('bf-quiz__option--selected'));
  }

  _advance() {
    if (this.currentStep >= this.steps.length) {
      this._showResults();
    } else {
      this.showStep(this.currentStep + 1);
    }
  }

  showStep(stepNumber, { keepResults = false } = {}) {
    this.currentStep = stepNumber;
    this.steps.forEach((el, index) => {
      el.hidden = index + 1 !== stepNumber;
    });
    // Nothing to go back to on the first step — keep the slot so the header does not jump.
    this.card.classList.toggle('bf-quiz__card--first-step', stepNumber === 1);
    this.card.hidden = false;
    if (!keepResults) this.resultsEl.hidden = true;

    if (stepNumber === this.steps.length) this._prefetchFallbacks();
  }

  reset({ keepResults = false } = {}) {
    this.answers = this._emptyAnswers();
    this._clearSelection();
    if (!keepResults) {
      this.gridEl.innerHTML = '';
      this.resultsEl.hidden = true;
    }
    this.showStep(1, { keepResults });
    this._scrollToCard();
  }

  /**
   * QA: on mobile the theme pins the header over the top of the viewport, so a
   * plain scrollIntoView tucks the question underneath it. Reserve the header
   * height through scroll-margin-top, then re-check once the scroll has stopped —
   * the theme's sticky plugin pins and unpins the header while we are animating,
   * so a single measurement up front is not enough to trust.
   */
  _scrollToCard() {
    this.card.style.scrollMarginTop = `${this._headerOffset() + BouquetQuiz.SCROLL_GAP}px`;
    this.card.scrollIntoView({ behavior: 'smooth', block: 'start' });

    this._whenScrollSettles(() => {
      const wanted = this._headerOffset() + BouquetQuiz.SCROLL_GAP;
      const covered = wanted - this.card.getBoundingClientRect().top;
      // only ever corrects upwards, so it cannot overshoot the card
      if (covered > 1) window.scrollBy({ top: -covered, behavior: 'smooth' });
    });
  }

  /**
   * Height the sticky theme header takes off the top of the viewport. A header
   * that is wrapped by the sticky plugin but not pinned right now still counts:
   * it re-pins as soon as the page moves, and the card must clear it.
   */
  _headerOffset() {
    let offset = 0;

    document.querySelectorAll('#mobile-header, #header').forEach((el) => {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') return;

      const rect = el.getBoundingClientRect();
      const isPinned =
        (style.position === 'fixed' || style.position === 'sticky') &&
        rect.top <= 1 &&
        rect.bottom > 0;

      if (isPinned) {
        offset = Math.max(offset, rect.bottom);
      } else if (el.parentElement && el.parentElement.classList.contains('header-sticky-wrapper')) {
        offset = Math.max(offset, el.offsetHeight);
      }
    });

    return offset;
  }

  /** Run `callback` once the page has stopped scrolling (2s safety net). */
  _whenScrollSettles(callback) {
    let previousY = null;
    let ticks = 0;

    const check = () => {
      const y = Math.round(window.scrollY);
      if (y === previousY || ticks > 40) {
        callback();
        return;
      }
      previousY = y;
      ticks += 1;
      window.setTimeout(check, 50);
    };

    // give the smooth scroll a frame or two to start before sampling
    window.setTimeout(check, 100);
  }

  _clearSelection() {
    this.steps.forEach((stepEl) =>
      stepEl
        .querySelectorAll('[data-quiz-option]')
        .forEach((el) => el.classList.remove('bf-quiz__option--selected'))
    );
  }

  /**
   * The card stays on screen after the last step and rewinds to step 1, so the
   * quiz can be retaken without hunting for it; the results render underneath.
   */
  async _showResults() {
    this.resultsEl.hidden = false;
    this.showStep(1, { keepResults: true });
    await this._fetchAndRender();
    // The run is over: step 1 must read as a fresh start, not as the last answers.
    this.answers = this._emptyAnswers();
    this._clearSelection();
  }

  /* ── Collection Section Rendering API ───────────────────── */

  /**
   * Storefront price filters expect currency units (pounds), which is what the
   * option data attributes already hold.
   */
  _buildFilterParams({ priceMin, priceMax, color, style }) {
    const params = new URLSearchParams();

    if (priceMin !== null && priceMin > 0) params.set('filter.v.price.gte', String(priceMin));
    if (priceMax !== null) params.set('filter.v.price.lte', String(priceMax));
    if (color) params.set('filter.v.m.quiz.color', color);
    if (style) params.set('filter.v.m.quiz.style', style);

    return params;
  }

  _collectionUrl(filterParams, page = 1) {
    const url = new URL(`/collections/${this.collection}`, window.location.origin);
    url.searchParams.set('sections', 'collection__main');
    if (this.sort) url.searchParams.set('sort_by', this.sort);
    if (page > 1) url.searchParams.set('page', String(page));
    for (const [key, value] of filterParams.entries()) {
      url.searchParams.append(key, value);
    }

    return url.toString();
  }

  _fetchCollectionHTML(filterParams, page = 1) {
    const href = this._collectionUrl(filterParams, page);
    const cached = this._htmlCache.get(href);
    if (cached) return cached;

    const request = fetch(href)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => data['collection__main'] ?? '')
      .catch((error) => {
        // a failed request must not poison the cache
        this._htmlCache.delete(href);
        throw error;
      });

    this._htmlCache.set(href, request);

    return request;
  }

  /**
   * Warm the fallback passes while the user is still on the last step.
   * Only the style filter is unknown at that point, so the "drop style" and
   * "price only" queries can be fetched ahead of the final click.
   */
  _prefetchFallbacks() {
    const { priceMin, priceMax, color } = this.answers;
    const combos = [{ priceMin, priceMax, color, style: null }];
    if (color) combos.push({ priceMin, priceMax, color: null, style: null });

    combos.forEach((combo) => {
      this._fetchCollectionHTML(this._buildFilterParams(combo)).catch(() => {});
    });
  }

  /** Walk collection pages for one filter combination until `needed` cards are collected. */
  async _fetchCards(filterParams, needed, seen) {
    const collected = [];
    let page = 1;

    while (collected.length < needed) {
      const html = await this._fetchCollectionHTML(filterParams, page);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const batch = Array.from(doc.querySelectorAll('.product__grid-item'));
      if (batch.length === 0) break;

      for (const card of batch) {
        if (collected.length >= needed) break;
        const key = this._cardKey(card);
        if (!key || seen.has(key)) continue;
        if (!this._priceMatches(card)) continue;
        seen.add(key);
        collected.push(card);
      }

      const paginateEl = doc.querySelector('.paginate[data-paginate-pages]');
      const totalPages = paginateEl ? parseInt(paginateEl.dataset.paginatePages, 10) : 1;
      const currentPage = paginateEl ? parseInt(paginateEl.dataset.currentPage, 10) : page;
      if (currentPage >= totalPages) break;

      page += 1;
    }

    return collected;
  }

  /** Product handle — stable dedup key across pages and filter passes. */
  _cardKey(cardEl) {
    const link = cardEl.querySelector('a.product-wrap__link, a[href*="/products/"]');
    if (!link) return null;
    const match = (link.getAttribute('href') || '').match(/\/products\/([^?#/]+)/);

    return match ? match[1] : null;
  }

  /**
   * Belt-and-braces price check: the card carries the product's cheapest variant
   * price in pence, so a relaxed filter pass can never leak an out-of-budget bouquet.
   * Cards without the attribute are allowed through.
   */
  _priceMatches(cardEl) {
    const { priceMin, priceMax } = this.answers;
    if (priceMin === null && priceMax === null) return true;

    const raw = cardEl.getAttribute('data-quiz-min-price');
    if (raw === null) return true;

    const pence = parseInt(raw, 10);
    if (isNaN(pence)) return true;
    if (priceMin !== null && pence < priceMin * 100) return false;
    if (priceMax !== null && pence > priceMax * 100) return false;

    return true;
  }

  /** Up to three passes, relaxing style then colour. Price is never dropped. */
  async _resolveCards() {
    const { priceMin, priceMax, color, style } = this.answers;
    const seen = new Set();
    const cards = [];
    const need = () => this.limit - cards.length;

    const run = async (params) => {
      cards.push(...(await this._fetchCards(params, need(), seen)));
    };

    await run(this._buildFilterParams({ priceMin, priceMax, color, style }));

    if (cards.length < this.limit && style) {
      await run(this._buildFilterParams({ priceMin, priceMax, color, style: null }));
    }

    if (cards.length < this.limit && (color || style)) {
      await run(this._buildFilterParams({ priceMin, priceMax, color: null, style: null }));
    }

    return cards;
  }

  async _fetchAndRender() {
    this.loadingEl.hidden = false;
    this.gridEl.innerHTML = '';
    this.setAttribute('data-loading', '');

    try {
      const cards = await this._resolveCards();

      if (cards.length === 0) {
        this._renderTemplate(this.emptyTpl);
        return;
      }

      cards.forEach((card) => {
        // The label under the price comes from the collection template and can be
        // rewritten by the subscription app, so pin it to the section setting.
        if (this.subscriptionLabel) {
          card.querySelectorAll('.subscription .text').forEach((el) => {
            el.textContent = this.subscriptionLabel;
          });
        }

        // All eight cards are visible at once, so resolve lazy images right away.
        card.querySelectorAll('img[data-src]').forEach((img) => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
        });
        this.gridEl.appendChild(card);
      });
    } catch (error) {
      console.error('[BouquetQuiz]', error);
      this._renderTemplate(this.errorTpl);
    } finally {
      this.loadingEl.hidden = true;
      this.removeAttribute('data-loading');
    }
  }

  _renderTemplate(template) {
    if (template) this.gridEl.appendChild(template.content.cloneNode(true));
  }
}

if (!window.customElements.get('bouquet-quiz')) {
  window.customElements.define('bouquet-quiz', BouquetQuiz);
}
